// Admin reporting per the July-2026 mock: date-range filters with preset
// chips, icon stat tiles with a trend vs the equal-length previous period,
// per-day bars, and by-user / by-template breakdowns. "Export report"
// downloads the visible range as CSV, built client-side from the same data.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BriefcaseBusiness,
  Download,
  FileText,
  FileDown,
  UploadCloud,
  Sparkles,
  UserCheck,
  Users,
  LayoutGrid,
} from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";
import { cn } from "@/lib/utils";

interface Stats {
  from: string;
  to: string;
  totals: {
    documents: number;
    docx: number;
    pdf: number;
    resumesUploaded: number;
    resumesExtracted: number;
    jdsCreated: number;
  };
  byDay: Array<{ day: string; docx: number; pdf: number }>;
  byUser: Array<{ email: string; count: number }>;
  byTemplate: Array<{ templateId: string; version: number; count: number }>;
  byOwner: Array<{ owner: string | null; count: number }>;
}

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** The equal-length window that ends the day before `from`. */
function previousRange(from: string, to: string): { from: string; to: string } {
  const DAY = 24 * 60 * 60 * 1000;
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  const len = Math.max(DAY, toMs - fromMs + DAY);
  return {
    from: new Date(fromMs - len).toISOString().slice(0, 10),
    to: new Date(fromMs - DAY).toISOString().slice(0, 10),
  };
}

function shortRange(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const a = new Date(from);
  const b = new Date(to);
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  return sameMonth
    ? `${a.toLocaleDateString(undefined, opts)}–${b.getDate()}`
    : `${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, opts)}`;
}

function trendFor(
  current: number,
  previous: number | undefined,
  label: string,
): { pct: number; label: string } | null {
  if (previous == null || previous === 0) return null;
  return { pct: Math.round(((current - previous) / previous) * 100), label };
}

function exportCsv(stats: Stats): void {
  const esc = (v: string | number) => `"${String(v).replaceAll('"', '""')}"`;
  const lines = [
    ["Range", stats.from, stats.to].map(esc).join(","),
    "",
    "Totals",
    ["documents", stats.totals.documents].map(esc).join(","),
    ["docx", stats.totals.docx].map(esc).join(","),
    ["pdf", stats.totals.pdf].map(esc).join(","),
    ["resumes_uploaded", stats.totals.resumesUploaded].map(esc).join(","),
    ["resumes_extracted", stats.totals.resumesExtracted].map(esc).join(","),
    ["jds_created", stats.totals.jdsCreated].map(esc).join(","),
    "",
    "By day,docx,pdf",
    ...stats.byDay.map((d) => [d.day, d.docx, d.pdf].map(esc).join(",")),
    "",
    "By user,documents",
    ...stats.byUser.map((u) => [u.email, u.count].map(esc).join(",")),
    "",
    "By template,version,documents",
    ...stats.byTemplate.map((t) => [t.templateId, t.version, t.count].map(esc).join(",")),
    "",
    "By candidate owner,resumes_and_jds",
    ...stats.byOwner.map((o) => [o.owner ?? "Unassigned", o.count].map(esc).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `agp-report-${stats.from}-to-${stats.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Decorative chart art in the header, per the mock. Pure ornament.
function HeaderArt() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 220 90"
      className="pointer-events-none absolute right-0 top-0 hidden h-24 w-56 text-brand-400 opacity-50 md:block"
      fill="none"
    >
      {[10, 38, 66, 94, 122].map((x, i) => (
        <rect
          key={x}
          x={x}
          y={70 - [22, 38, 30, 52, 44][i]!}
          width="16"
          height={[22, 38, 30, 52, 44][i]}
          rx="3"
          fill="currentColor"
          opacity={0.35}
        />
      ))}
      <path
        d="M8 58 C 50 48, 90 60, 125 34 S 195 6, 216 14"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity={0.7}
      />
      <rect x="156" y="26" width="52" height="36" rx="8" fill="currentColor" opacity={0.18} />
      <circle cx="172" cy="44" r="9" fill="currentColor" opacity={0.5} />
      <rect x="186" y="36" width="16" height="4" rx="2" fill="currentColor" opacity={0.5} />
      <rect x="186" y="44" width="12" height="4" rx="2" fill="currentColor" opacity={0.35} />
    </svg>
  );
}

function BreakdownCard({
  icon,
  title,
  nameHeader,
  countHeader = "Documents",
  rows,
}: {
  icon: React.ReactNode;
  title: string;
  nameHeader: string;
  countHeader?: string;
  rows: Array<{ key: string; name: React.ReactNode; count: number }>;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2 text-base">
          <span className="text-brand-on">{icon}</span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardBody className="px-0 py-0">
        <div className="flex items-center justify-between border-b border-border px-5 pb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-text-2">
          <span>{nameHeader}</span>
          <span>{countHeader}</span>
        </div>
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.key} className="flex items-center gap-3 px-5 py-2.5">
              <span className="min-w-0 flex-1 truncate text-[14px] text-text">{r.name}</span>
              <span className="text-[14px] font-semibold tabular-nums text-text">{r.count}</span>
              <span
                className="h-2 shrink-0 rounded-full bg-brand-500"
                style={{ width: `${Math.max(6, (r.count / max) * 90)}px` }}
                aria-hidden
              />
            </li>
          ))}
          {rows.length === 0 && (
            <li className="px-5 py-4 text-[14px] text-text-3">No documents yet.</li>
          )}
        </ul>
      </CardBody>
    </Card>
  );
}

export function Reports() {
  const api = useApi();
  const me = useMe();
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [preset, setPreset] = useState<number | null>(30);

  const statsQuery = useQuery({
    queryKey: ["admin-stats", from, to],
    queryFn: () => api.get<Stats>(`/api/admin/stats?from=${from}&to=${to}`),
    enabled: me?.role === "admin",
  });
  const prev = previousRange(from, to);
  const prevQuery = useQuery({
    queryKey: ["admin-stats", prev.from, prev.to],
    queryFn: () => api.get<Stats>(`/api/admin/stats?from=${prev.from}&to=${prev.to}`),
    enabled: me?.role === "admin",
  });

  if (me && me.role !== "admin") {
    return (
      <p className="py-10 text-center text-[14px] text-text-3">
        Reports need an admin account.
      </p>
    );
  }

  const stats = statsQuery.data;
  const prevTotals = prevQuery.data?.totals;
  const vsLabel = `vs ${shortRange(prev.from, prev.to)}`;
  const maxDay = Math.max(1, ...(stats?.byDay.map((d) => d.docx + d.pdf) ?? [1]));

  return (
    <div className="flex flex-col gap-5">
      <header className="relative">
        <HeaderArt />
        <h2 className="mb-1">Reports</h2>
        <p className="max-w-xl text-[14px] text-text-2">
          Documents generated and resumes processed. Counts reflect stored outputs; the audit
          log keeps the full history even after deletion.
        </p>
      </header>

      <Card>
        <CardBody className="flex flex-wrap items-end gap-3 py-4">
          <div>
            <Label htmlFor="from">From</Label>
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPreset(null);
              }}
            />
          </div>
          <div>
            <Label htmlFor="to">To</Label>
            <Input
              id="to"
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPreset(null);
              }}
            />
          </div>
          {([7, 30, 90] as const).map((days) => (
            <Button
              key={days}
              variant="secondary"
              size="sm"
              className={cn(
                preset === days && "border-transparent bg-brand-tint text-brand-on hover:bg-brand-tint",
              )}
              onClick={() => {
                setFrom(isoDaysAgo(days));
                setTo(isoDaysAgo(0));
                setPreset(days);
              }}
            >
              {days} days
            </Button>
          ))}
          <div className="ml-auto">
            <Button variant="secondary" disabled={!stats} onClick={() => stats && exportCsv(stats)}>
              <Download size={15} strokeWidth={1.75} aria-hidden />
              Export report
            </Button>
          </div>
        </CardBody>
      </Card>

      {statsQuery.isLoading && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <StatCard
              label="Documents"
              value={stats.totals.documents}
              tone="ok"
              valueToned={false}
              icon={<FileText size={19} strokeWidth={1.75} aria-hidden />}
              trend={trendFor(stats.totals.documents, prevTotals?.documents, vsLabel)}
              caption={vsLabel}
            />
            <StatCard
              label="DOCX"
              value={stats.totals.docx}
              tone="accent"
              valueToned={false}
              icon={<span className="font-display text-[15px] font-bold">W</span>}
              trend={trendFor(stats.totals.docx, prevTotals?.docx, vsLabel)}
              caption={vsLabel}
            />
            <StatCard
              label="PDF"
              value={stats.totals.pdf}
              tone="danger"
              valueToned={false}
              icon={<FileDown size={19} strokeWidth={1.75} aria-hidden />}
              trend={trendFor(stats.totals.pdf, prevTotals?.pdf, vsLabel)}
              caption={vsLabel}
            />
            <StatCard
              label="Resumes uploaded"
              value={stats.totals.resumesUploaded}
              tone="purple"
              valueToned={false}
              icon={<UploadCloud size={19} strokeWidth={1.75} aria-hidden />}
              trend={trendFor(stats.totals.resumesUploaded, prevTotals?.resumesUploaded, vsLabel)}
              caption={vsLabel}
            />
            <StatCard
              label="Extractions done"
              value={stats.totals.resumesExtracted}
              tone="orange"
              valueToned={false}
              icon={<Sparkles size={19} strokeWidth={1.75} aria-hidden />}
              trend={trendFor(stats.totals.resumesExtracted, prevTotals?.resumesExtracted, vsLabel)}
              caption={vsLabel}
            />
            <StatCard
              label="Job descriptions"
              value={stats.totals.jdsCreated}
              tone="accent"
              valueToned={false}
              icon={<BriefcaseBusiness size={19} strokeWidth={1.75} aria-hidden />}
              trend={trendFor(stats.totals.jdsCreated, prevTotals?.jdsCreated, vsLabel)}
              caption={vsLabel}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">By day</CardTitle>
            </CardHeader>
            <CardBody className="px-0 py-0">
              {stats.byDay.length === 0 ? (
                <p className="px-5 py-6 text-[14px] text-text-3">
                  Nothing generated in this range.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {stats.byDay.map((d) => (
                    <li key={d.day} className="flex items-center gap-4 px-5 py-2.5">
                      <span className="w-24 shrink-0 text-[13px] tabular-nums text-text-2">
                        {d.day}
                      </span>
                      <span
                        className="h-2.5 rounded-full bg-brand-500"
                        style={{ width: `${Math.max(2, ((d.docx + d.pdf) / maxDay) * 55)}%` }}
                        aria-hidden
                      />
                      <span className="whitespace-nowrap text-[13px] tabular-nums text-text">
                        {d.docx + d.pdf}
                        <span className="text-text-3">
                          {" "}
                          ({d.docx} docx / {d.pdf} pdf)
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <BreakdownCard
              icon={<Users size={16} strokeWidth={1.75} aria-hidden />}
              title="By user"
              nameHeader="User"
              rows={stats.byUser.map((u) => ({ key: u.email, name: u.email, count: u.count }))}
            />
            <BreakdownCard
              icon={<LayoutGrid size={16} strokeWidth={1.75} aria-hidden />}
              title="By template"
              nameHeader="Template"
              rows={stats.byTemplate.map((t) => ({
                key: `${t.templateId}-${t.version}`,
                name: (
                  <>
                    {t.templateId} <span className="text-text-3">v{t.version}</span>
                  </>
                ),
                count: t.count,
              }))}
            />
            <BreakdownCard
              icon={<UserCheck size={16} strokeWidth={1.75} aria-hidden />}
              title="By candidate owner"
              nameHeader="Owner"
              countHeader="Resumes + JDs"
              rows={stats.byOwner.map((o) => ({
                key: o.owner ?? "—",
                name: o.owner ?? <span className="text-text-3">Unassigned</span>,
                count: o.count,
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
}
